import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, FileText, Info, MessageSquare, Paperclip, X } from 'lucide-react';
import { z } from 'zod';
import { toast } from 'sonner';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { cn } from '@/nucleo/utilitarios';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/ui/base/collapsible';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { SearchableSelect } from '@/ui/base/searchable-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Textarea } from '@/ui/base/textarea';
import { ResponsiveModal } from '@/ui/ResponsiveModal';

import { useDres } from '../hooks/useDres';
import { ROTULO_TIPO, TIPOS_QUE_EXIGEM_ESCOLA } from './apresentacao';
import { type ModeloDeSolicitacao, useEscolasDaDre, useModelosDeSolicitacao } from './dados';
import type { OrigemSolicitacaoSme, TipoSolicitacaoSme } from './tipos';

const MAX_ANEXOS = 5;
const MAX_MB = 10;

function hojeLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Mesmas regras do esquema do protótipo (`SmeDemandaForm`). */
const esquema = z
  .object({
    tipo: z.string().min(1, 'Selecione o tipo'),
    dreId: z.string().min(1, 'Selecione uma DRE'),
    escolaId: z.string().nullable(),
    titulo: z.string().trim().min(5, 'Mínimo 5 caracteres').max(140, 'Máximo 140 caracteres'),
    justificativa: z
      .string()
      .trim()
      .min(20, 'Descreva com pelo menos 20 caracteres')
      .max(2000, 'Máximo 2000 caracteres'),
    prazo: z
      .string()
      .nullable()
      .refine((v) => !v || v >= hojeLocal(), { message: 'Prazo deve ser hoje ou futuro' }),
  })
  .refine((v) => !TIPOS_QUE_EXIGEM_ESCOLA.includes(v.tipo as TipoSolicitacaoSme) || !!v.escolaId, {
    message: 'Escola é obrigatória para este tipo de demanda',
    path: ['escolaId'],
  });

type Campo = 'tipo' | 'dreId' | 'escolaId' | 'titulo' | 'justificativa' | 'prazo';

interface AnexoSelecionado {
  id: string;
  arquivo: File;
}

interface NovaSolicitacaoSmeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Sentido da solicitação — muda título, texto e modelos. */
  origem: OrigemSolicitacaoSme;
}

/**
 * Nova solicitação, nos dois sentidos — `SmeDemandaFormModal` + `SmeDemandaForm`
 * do protótipo.
 *
 * NÃO GRAVA. Não há API de `sme_demandas`: o envio valida o formulário e mostra
 * o aviso de indisponibilidade, sem fechar o modal nem navegar — navegar para um
 * detalhe seria fingir que a solicitação existe. Um aviso fixo acompanha o
 * botão, porque aviso passageiro some e a captura de tela fica parecendo envio.
 * Os anexos ficam só no formulário.
 */
export function NovaSolicitacaoSmeModal({
  open,
  onOpenChange,
  origem,
}: NovaSolicitacaoSmeModalProps) {
  const { data: dres } = useDres();
  const { data: modelos } = useModelosDeSolicitacao(origem);

  const [modelosAbertos, definirModelosAbertos] = useState(false);
  const [modeloAplicado, definirModeloAplicado] = useState<string | null>(null);
  const [tipo, definirTipo] = useState<TipoSolicitacaoSme | ''>('');
  const [dreId, definirDreId] = useState<string | null>(null);
  const [escolaId, definirEscolaId] = useState<string | null>(null);
  const [titulo, definirTitulo] = useState('');
  const [justificativa, definirJustificativa] = useState('');
  const [prazo, definirPrazo] = useState('');
  const [anexos, definirAnexos] = useState<AnexoSelecionado[]>([]);
  const [erros, definirErros] = useState<Partial<Record<Campo, string>>>({});
  const [erroDeAnexo, definirErroDeAnexo] = useState<string | null>(null);

  const { data: escolas, isFetching: carregandoEscolas } = useEscolasDaDre(dreId);

  const sujo =
    tipo !== '' ||
    dreId !== null ||
    titulo !== '' ||
    justificativa !== '' ||
    prazo !== '' ||
    anexos.length > 0;

  useEffect(() => {
    if (open) return;
    definirTipo('');
    definirDreId(null);
    definirEscolaId(null);
    definirTitulo('');
    definirJustificativa('');
    definirPrazo('');
    definirAnexos([]);
    definirErros({});
    definirErroDeAnexo(null);
    definirModelosAbertos(false);
    definirModeloAplicado(null);
  }, [open]);

  const escolaObrigatoria = tipo !== '' && TIPOS_QUE_EXIGEM_ESCOLA.includes(tipo);

  const opcoesDeEscola = useMemo(
    () =>
      (escolas ?? []).map((e) => ({
        value: e.id,
        label: e.nome,
        code: e.codigoEol,
        description: e.bairro,
      })),
    [escolas],
  );

  function aplicarModelo(modelo: ModeloDeSolicitacao) {
    definirModeloAplicado(modelo.id);
    definirTipo(modelo.tipo);
    definirTitulo(modelo.tituloSugerido.slice(0, 140));
    definirJustificativa(modelo.justificativaSugerida);
    toast('Modelo aplicado', { description: 'Edite os campos antes de enviar.' });
  }

  function adicionarAnexos(arquivos: FileList | null) {
    definirErroDeAnexo(null);
    if (!arquivos || arquivos.length === 0) return;
    const lista = Array.from(arquivos);
    if (anexos.length + lista.length > MAX_ANEXOS) {
      definirErroDeAnexo(`Envie no máximo ${MAX_ANEXOS} arquivos.`);
      return;
    }
    const grande = lista.find((a) => a.size > MAX_MB * 1024 * 1024);
    if (grande) {
      definirErroDeAnexo(`"${grande.name}" passa de ${MAX_MB} MB. Escolha um arquivo menor.`);
      return;
    }
    definirAnexos((atual) => [
      ...atual,
      ...lista.map((arquivo) => ({ id: `${arquivo.name}-${arquivo.size}-${Date.now()}`, arquivo })),
    ]);
  }

  function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    const resultado = esquema.safeParse({
      tipo,
      dreId: dreId ?? '',
      escolaId,
      titulo,
      justificativa,
      prazo: prazo || null,
    });
    if (!resultado.success) {
      const novos: Partial<Record<Campo, string>> = {};
      for (const falha of resultado.error.issues) {
        const campo = falha.path[0] as Campo;
        novos[campo] ??= falha.message;
      }
      definirErros(novos);
      toast.error('Verifique os campos', { description: Object.values(novos)[0] });
      return;
    }
    definirErros({});
    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
  }

  const tituloDoModal = origem === 'DTP' ? 'Nova solicitação DTP → SME' : 'Nova demanda SME → DTP';
  const descricao =
    origem === 'DTP'
      ? 'Cobrança, esclarecimento ou pedido formal para a SME-DRE.'
      : 'Registre uma solicitação formal para a equipe de transporte.';
  const rotuloDoEnvio = origem === 'DTP' ? 'Enviar solicitação' : 'Registrar demanda';

  const erro = (campo: Campo) =>
    erros[campo] ? (
      <p id={`sol-${campo}-erro`} className="mt-1 text-xs text-destructive">
        {erros[campo]}
      </p>
    ) : null;

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={
        <>
          <MessageSquare className="h-5 w-5 text-primary" aria-hidden="true" />
          {tituloDoModal}
        </>
      }
      description={descricao}
      desktopMaxWidth="max-w-3xl"
      confirmOnClose={sujo}
    >
      <form className="space-y-4" onSubmit={enviar} noValidate>
        {modelos && modelos.length > 0 && (
          <Card className="border-dashed">
            <CardContent className="space-y-2 p-3">
              <Collapsible open={modelosAbertos} onOpenChange={definirModelosAbertos}>
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 rounded-sm text-sm font-medium focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="inline-flex items-center gap-2">
                      <FileText className="h-4 w-4 text-primary" aria-hidden="true" />
                      Usar modelo pronto ({modelos.length} disponíveis)
                    </span>
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 shrink-0 transition-transform',
                        modelosAbertos && 'rotate-180',
                      )}
                      aria-hidden="true"
                    />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2">
                  <div className="grid gap-2 sm:grid-cols-2">
                    {modelos.map((modelo) => (
                      <button
                        key={modelo.id}
                        type="button"
                        onClick={() => aplicarModelo(modelo)}
                        aria-pressed={modeloAplicado === modelo.id}
                        className={cn(
                          'rounded-lg border p-2.5 text-left transition hover:border-primary hover:bg-muted/50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
                          modeloAplicado === modelo.id
                            ? 'border-primary bg-primary/5'
                            : 'border-border',
                        )}
                      >
                        <span className="mb-1 flex items-center justify-between gap-2">
                          <span className="text-sm font-medium leading-tight">{modelo.nome}</span>
                          <Badge variant="outline" className="shrink-0 text-[10px]">
                            {ROTULO_TIPO[modelo.tipo]}
                          </Badge>
                        </span>
                        <span className="line-clamp-2 block text-xs text-muted-foreground">
                          {modelo.tituloSugerido}
                        </span>
                      </button>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
              {!modelosAbertos && modeloAplicado && (
                <p className="text-xs text-muted-foreground">
                  Modelo aplicado. Edite os campos abaixo conforme necessário.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        <fieldset className="space-y-4 rounded-xl border p-4">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Detalhes
          </legend>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="sol-tipo">Tipo *</Label>
              <Select value={tipo} onValueChange={(v) => definirTipo(v as TipoSolicitacaoSme)}>
                <SelectTrigger
                  id="sol-tipo"
                  aria-invalid={Boolean(erros.tipo)}
                  aria-describedby={erros.tipo ? 'sol-tipo-erro' : undefined}
                >
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ROTULO_TIPO).map(([valor, rotulo]) => (
                    <SelectItem key={valor} value={valor}>
                      {rotulo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {erro('tipo')}
            </div>
            <div>
              <Label>DRE *</Label>
              <SearchableSelect
                value={dreId}
                onChange={(v) => {
                  definirDreId(v);
                  definirEscolaId(null);
                }}
                options={(dres ?? []).map((d) => ({
                  value: d.id,
                  label: d.nome ?? d.codigo ?? d.id,
                }))}
                placeholder="Buscar DRE…"
                ariaLabel="DRE"
              />
              {erro('dreId')}
            </div>
          </div>

          <div>
            <Label>
              Escola{' '}
              {escolaObrigatoria ? '*' : <span className="text-muted-foreground">(opcional)</span>}
            </Label>
            <SearchableSelect
              value={escolaId}
              onChange={definirEscolaId}
              options={opcoesDeEscola}
              loading={carregandoEscolas}
              disabled={!dreId}
              placeholder={
                !dreId
                  ? 'Selecione a DRE primeiro'
                  : opcoesDeEscola.length === 0 && !carregandoEscolas
                    ? 'Nenhuma escola ativa nesta DRE'
                    : 'Buscar escola…'
              }
              searchPlaceholder="Digite nome ou código EOL"
              emptyMessage="Nenhuma escola encontrada"
              clearable
              ariaLabel="Escola"
            />
            {erro('escolaId')}
            <p className="mt-1 text-xs text-muted-foreground">
              {escolaObrigatoria
                ? 'Tipo selecionado exige escola específica.'
                : 'Informe a escola apenas se a demanda for sobre uma unidade específica.'}
            </p>
          </div>

          <div>
            <Label htmlFor="sol-titulo">Título *</Label>
            <Input
              id="sol-titulo"
              value={titulo}
              onChange={(e) => definirTitulo(e.target.value)}
              placeholder="Resumo da solicitação"
              maxLength={140}
              aria-invalid={Boolean(erros.titulo)}
              aria-describedby={erros.titulo ? 'sol-titulo-erro' : undefined}
            />
            <div className="mt-1 flex justify-between gap-2">
              {erro('titulo') ?? <span />}
              <p className="text-xs text-muted-foreground">{titulo.length}/140</p>
            </div>
          </div>

          <div>
            <Label htmlFor="sol-justificativa">Justificativa *</Label>
            <Textarea
              id="sol-justificativa"
              value={justificativa}
              onChange={(e) => definirJustificativa(e.target.value)}
              placeholder="Descreva a necessidade, contexto e impacto"
              rows={5}
              maxLength={2000}
              aria-invalid={Boolean(erros.justificativa)}
              aria-describedby={erros.justificativa ? 'sol-justificativa-erro' : undefined}
            />
            <div className="mt-1 flex justify-between gap-2">
              {erro('justificativa') ?? <span />}
              <p className="text-xs text-muted-foreground">{justificativa.length}/2000</p>
            </div>
          </div>

          <div className="max-w-xs">
            <Label htmlFor="sol-prazo">Prazo desejado</Label>
            <Input
              id="sol-prazo"
              type="date"
              value={prazo}
              min={hojeLocal()}
              onChange={(e) => definirPrazo(e.target.value)}
              aria-invalid={Boolean(erros.prazo)}
              aria-describedby={erros.prazo ? 'sol-prazo-erro' : undefined}
            />
            {erro('prazo')}
          </div>

          <div className="space-y-2">
            <Label htmlFor="sol-anexos">
              Anexos <span className="text-muted-foreground">(opcional)</span>
            </Label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                id="sol-anexos"
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
                className="sr-only"
                disabled={!dreId || anexos.length >= MAX_ANEXOS}
                onChange={(e) => {
                  adicionarAnexos(e.target.files);
                  e.target.value = '';
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!dreId || anexos.length >= MAX_ANEXOS}
                onClick={() => document.getElementById('sol-anexos')?.click()}
              >
                <Paperclip className="mr-1 h-4 w-4" aria-hidden="true" />
                Adicionar anexo
              </Button>
              <span className="text-xs text-muted-foreground">
                Até {MAX_ANEXOS} arquivos · {MAX_MB} MB cada · PDF/imagem/DOC/XLS
              </span>
            </div>
            {erroDeAnexo && <p className="text-xs text-destructive">{erroDeAnexo}</p>}
            {anexos.length > 0 && (
              <ul className="mt-2 space-y-1.5">
                {anexos.map((anexo) => (
                  <li
                    key={anexo.id}
                    className="flex items-center justify-between gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-sm"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <FileText
                        className="h-4 w-4 shrink-0 text-muted-foreground"
                        aria-hidden="true"
                      />
                      <span className="truncate">{anexo.arquivo.name}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {(anexo.arquivo.size / 1024).toFixed(0)} KB
                      </span>
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        definirAnexos((atual) => atual.filter((a) => a.id !== anexo.id))
                      }
                      aria-label={`Remover ${anexo.arquivo.name}`}
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </fieldset>

        <Alert>
          <Info className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>
            O envio de solicitações ainda não está disponível. Você pode preencher e conferir o
            formulário, mas nada é registrado.
          </AlertDescription>
        </Alert>

        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="submit">{rotuloDoEnvio}</Button>
        </div>
      </form>
    </ResponsiveModal>
  );
}

export default NovaSolicitacaoSmeModal;
